import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Title, Text, SimpleGrid, Card, Image, Button, Badge, Loader, Center, Group, createStyles, Paper, Stack } from '@mantine/core';
import { IconArrowLeft, IconShoppingCart } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
    wrapper: {
        paddingTop: 100,
        paddingBottom: 80,
        minHeight: '100vh',
        backgroundColor: '#050505',
        backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
        backgroundAttachment: 'fixed',
        color: theme.white,
    },
    glassPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: theme.radius.lg,
        overflow: 'hidden',
    },
    heroSection: {
        position: 'relative',
        height: 400,
        borderRadius: theme.radius.lg,
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: theme.spacing.xl * 2,
    },
    productCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
        }
    }
}));

const DropPage = () => {
    const { classes } = useStyles();
    const { id } = useParams();
    const navigate = useNavigate();
    const [drop, setDrop] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`https://backend.cdp3d.com/api/drops`)
            .then(res => res.json())
            .then(data => {
                const found = data.find(d => d._id === id);
                setDrop(found);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [id]);

    if (loading) return (
        <div className={classes.wrapper}>
            <Center h="80vh"><Loader color="cyan" /></Center>
        </div>
    );

    if (!drop) return (
        <div className={classes.wrapper}>
            <Center h="80vh">
                <Stack align="center">
                    <Title>Drop Not Found</Title>
                    <Button onClick={() => navigate('/')} variant="outline" color="gray">Go Home</Button>
                </Stack>
            </Center>
        </div>
    );

    const bannerUrl = drop.bannerImage?.startsWith('http') ? drop.bannerImage : `https://backend.cdp3d.com${drop.bannerImage}`;

    return (
        <div className={classes.wrapper}>
            <Container size="xl">
                <Button
                    variant="subtle"
                    color="gray"
                    leftIcon={<IconArrowLeft size={16} />}
                    mb="lg"
                    onClick={() => navigate('/')}
                >
                    Back to Home
                </Button>

                {/* Hero Banner */}
                <div className={classes.heroSection}>
                    <div style={{ 
                        position: 'absolute', inset: 0,
                        backgroundImage: `url(${bannerUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.7)'
                    }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg, #050505 0%, transparent 80%)' }} />

                    <div style={{ position: 'absolute', bottom: 40, left: 40, right: 40, zIndex: 2 }}>
                        <Group mb="md">
                            <Badge size="xl" variant="gradient" gradient={{ from: 'red', to: 'orange' }}>
                                {drop.status === 'active' ? 'LIVE NOW' : drop.status.toUpperCase()}
                            </Badge>
                        </Group>
                        <Title size={64} style={{ textShadow: '0 0 30px rgba(0,0,0,0.8)', lineHeight: 1 }}>{drop.name}</Title>
                        <Text size="xl" mt="md" style={{ maxWidth: 800, textShadow: '0 0 10px rgba(0,0,0,0.8)', opacity: 0.9 }}>
                            {drop.description}
                        </Text>
                    </div>
                </div>

                {/* Products Grid */}
                <Title order={2} mb="xl" color="white">Exclusive Collection</Title>

                {drop.products && drop.products.length > 0 ? (
                    <SimpleGrid cols={3} spacing="xl" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                        {drop.products.map(product => {
                            const coverUrl = product.coverImage?.startsWith('http') ? product.coverImage : `https://backend.cdp3d.com${product.coverImage}`;

                            return (
                                <Card key={product._id} padding="lg" radius="md" className={classes.productCard}>
                                    <Card.Section>
                                        <Image
                                            src={coverUrl || 'https://via.placeholder.com/300'}
                                            height={260}
                                            alt={product.title}
                                            fit="cover"
                                        />
                                    </Card.Section>

                                    <Group position="apart" mt="md" mb="xs">
                                        <Text weight={600} size="lg" color="white" lineClamp={1}>{product.title}</Text>
                                        <Badge variant="outline" color="cyan">Exclusive</Badge>
                                    </Group>

                                    <Text size="sm" color="dimmed" lineClamp={2} mb="md" style={{ minHeight: 40 }}>
                                        {product.description}
                                    </Text>

                                    <Group position="apart" mt="auto">
                                        <Text size="xl" weight={700} color="white">
                                            ₹{product.currentPrice}
                                        </Text>
                                        <Button
                                            component={Link}
                                            to={`/product/${product._id}`}
                                            variant="gradient"
                                            gradient={{ from: 'cyan', to: 'indigo' }}
                                        >
                                            View Drop
                                        </Button>
                                    </Group>
                                </Card>
                            );
                        })}
                    </SimpleGrid>
                ) : (
                        <Paper className={classes.glassPanel} p="xl">
                            <Center>
                                <Text color="dimmed">No products assigned to this drop yet.</Text>
                            </Center>
                        </Paper>
                )}
            </Container>
        </div>
    );
};

export default DropPage;
